import * as logClient from "@classdojo/log-client";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import React from "react";
import { Timer } from "src/pods/timer";
import alarm from "src/pods/timer/assets/alarm.mp3";
import MaterialIcon from "src/shared/components/MaterialIcon";
import logEvent from "src/utils/logEvent";

type TimerDisplayProps = {
  sendTime?: (time: Timer) => void;
  time?: Timer;
  fullscreen: boolean;
  shouldRingBell: boolean;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class TimerDisplay extends React.Component<TimerDisplayProps> {
  constructor(props: TimerDisplayProps) {
    super(props);
  }

  componentDidMount() {
    this.UNSAFE_componentWillReceiveProps(this.props);
    logEvent({
      eventName: "timer.result.open",
    });
  }

  componentWillUnmount() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    logEvent({
      eventName: "timer.result.close",
    });
  }

  render() {
    const { fullscreen } = this.props;
    const seconds = this.secondsRemaining();
    return (
      <section sx={pageStyle(fullscreen, seconds)}>
        <div sx={timerStyle}>
          <div sx={timeStyle(fullscreen)} role="timer" aria-live="polite" aria-atomic="true">
            {seconds >= 60
              ? `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`
              : seconds > 0
                ? seconds
                : autoTranslate("Time's Up!")}
          </div>
          <audio src={alarm} ref={(node) => (this.audioNode = node)} />
          <footer sx={timerControls}>
            <button
              data-name="timer_display:render:material_icon"
              onClick={this.resetTimer}
              aria-label={autoTranslate("Replay timer")}
              sx={buttonResetStyles}
            >
              <MaterialIcon icon="replay" sx={timerResetButton} />
            </button>
            {this.renderTimerControls(seconds)}
            <button
              data-name="timer_display:render:plus_ten"
              onClick={() => this.incrementTimer(10000)}
              aria-label={autoTranslate("Add 10 seconds to timer")}
              sx={buttonResetStyles}
            >
              <span sx={timerSideButton}>+10</span>
            </button>
          </footer>
        </div>
      </section>
    );
  }

  timer: NodeJS.Timeout | null | undefined;
  audioNode: HTMLAudioElement | null | undefined;

  renderTimerControls(seconds: number) {
    return this.timer && seconds > 0 ? (
      <button
        data-name="timer_display:render_timer_controls:pause_timer"
        onClick={this.toggleTimer}
        sx={buttonResetStyles}
        aria-label={autoTranslate("Pause timer")}
      >
        <MaterialIcon sx={timerMainButton(seconds)} icon="pause" />
      </button>
    ) : (
      <button
        data-name="timer_display:render_timer_controls:play_timer"
        onClick={this.toggleTimer}
        sx={buttonResetStyles}
        aria-label={autoTranslate("Play timer")}
      >
        <MaterialIcon sx={timerMainButton(seconds)} icon="play_arrow" />
      </button>
    );
  }

  UNSAFE_componentWillReceiveProps(nextProps: TimerDisplayProps) {
    const { seconds, startTime, endTime } = nextProps.time || {};
    if (!!this.timer !== !!(seconds && startTime && !endTime)) {
      if (this.timer) {
        clearInterval(this.timer);
        this.updateTime();
        this.timer = null;
      }
      // we update the timer multiple times a second to prevent timer drift from
      // causing unexpected rendering issues (like skipping seconds, or not updating every second)
      if (seconds && startTime && !endTime) {
        this.timer = setInterval(this.updateTime, 100);
      }
    }
  }

  secondsRemaining = () => {
    const { seconds, startTime, endTime } = this.props.time || { seconds: 0 };
    return seconds ? Math.max(Math.ceil(seconds - ((endTime || Date.now()) - (startTime || Date.now())) / 1000), 0) : 0;
  };

  sendTime = (details: Partial<Timer>) => {
    const now = Date.now();
    const time = { ...this.props.time, ...details };
    const remainingTime =
      time.startTime && time.seconds ? time.seconds * 1000 - ((time.endTime || now) - time.startTime) : null;
    time.remainingTime = remainingTime;
    if (time !== undefined) {
      this.props.sendTime?.(time);
    }
  };

  updateTime = () => {
    this.forceUpdate();
    const { shouldRingBell } = this.props;

    if (this.secondsRemaining() <= 0) {
      if (this.audioNode && shouldRingBell) {
        this.audioNode.play().catch(() => {
          logClient.sendMetrics([
            {
              type: "increment",
              value: 1,
              metricName: "toolkit.media.audio.play.error",
            },
          ]);
        });
      }
      this.sendTime({ endTime: Date.now() });
    }
  };

  toggleTimer = () => {
    if (this.secondsRemaining() <= 0) {
      return this.startTimer();
    }
    if (this.timer) {
      return this.pauseTimer();
    } else {
      return this.resumeTimer();
    }
  };

  startTimer = () => {
    return this.resumeTimer(true);
  };

  pauseTimer = () => {
    const { startTime } = this.props.time || {};
    const endTime = Date.now();
    this.sendTime({ startTime, endTime });
  };

  resumeTimer = (fromBeginning?: boolean) => {
    let { startTime, endTime } = this.props.time || {};

    if (fromBeginning || !startTime) {
      startTime = Date.now();
    } else if (startTime && endTime) {
      startTime = Date.now() - (endTime - startTime);
    }

    endTime = null;
    this.sendTime({ startTime, endTime });
  };

  stopTimer = () => {
    const startTime = null;
    const endTime = null;
    this.sendTime({ startTime, endTime });
  };

  incrementTimer = (ms: number) => {
    const { startTime } = this.props.time || {};
    const newStartTime = startTime ? startTime + ms : null;
    this.sendTime({ startTime: newStartTime, increment: ms });

    logEvent({
      eventName: "timer.result.increment",
    });
  };

  resetTimer = () => {
    const startTime = this.timer ? Date.now() : null;
    const endTime = null;
    this.sendTime({ startTime, endTime, reset: true });
  };
}

const pageStyle = (fullscreen: boolean, seconds: number): ThemeUIStyleObject =>
  fullscreen
    ? {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100vw",
        height: "100vh",
        backgroundColor: seconds <= 3 ? "dt_content_danger" : "transparent",
      }
    : {};

const timerStyle: ThemeUIStyleObject = {
  textAlign: "center",
  padding: RAW_cssValue("0 12px 20px 12px"),
};

const timeStyle = (fullscreen: boolean): ThemeUIStyleObject => ({
  margin: RAW_cssValue("0 auto"),
  lineHeight: "0.9",
  fontSize: fullscreen ? "288px" : "100px",
  fontWeight: "800",
});

const timerControls: ThemeUIStyleObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: RAW_cssValue("25px auto"),
};

const timerSideButton: ThemeUIStyleObject = {
  fontSize: "18px",
  fontWeight: "600",
  cursor: "pointer",
  border: RAW_cssValue("0"),
  backgroundColor: "transparent",
  padding: RAW_cssValue("5px 15px"),
  margin: RAW_cssValue("10px"),
};

const timerResetButton: ThemeUIStyleObject = {
  fontSize: "18px",
  fontWeight: "600",
  cursor: "pointer",
  border: "none",
  backgroundColor: "transparent",
  padding: RAW_cssValue("5px 15px"),
  fontFamily: "Material Icons",
  margin: RAW_cssValue("10px"),
};

const timerMainButton = (seconds: number): ThemeUIStyleObject => ({
  width: "64px",
  height: "64px",
  fontFamily: "Material Icons",
  fontSize: "40px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "dt_radius_round",
  backgroundColor: seconds <= 3 ? "dt_content_accent" : "dt_content_danger",
  color: RAW_cssValue("#000000"),
  boxShadow: "dt_shadow_shadezies_small",
  cursor: "pointer",
  border: RAW_cssValue("0"),
  margin: RAW_cssValue("5px"),
});

const buttonResetStyles: ThemeUIStyleObject = {
  margin: 0,
  padding: 0,
  border: "none",
  backgroundColor: "transparent",
  color: RAW_cssValue("#000000"),
};
